import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { customFetch, ApiError } from '../api/http/fetcher';
import { getLoungeToken } from '../utils/loungeToken';
import type { AnswersLoungeSurveyBody } from '../api/generated/models';
import type {
  LoungeSurveyResponse,
  UseLoungeSurveySubmitOptions,
} from '../types';

// Submit survey API call
const submitLoungeSurvey = async (
  body: AnswersLoungeSurveyBody,
  token: string,
): Promise<LoungeSurveyResponse> => {
  return customFetch<LoungeSurveyResponse>(
    `/answers/lounge_submit/?token=${encodeURIComponent(token)}`,
    {
      method: 'POST',
      body,
    },
  );
};

/**
 * Hook for handling lounge survey submission
 * Manages the mutation state, token validation, and navigation
 */
export const useLoungeSurveySubmit = (
  options: UseLoungeSurveySubmitOptions = {},
) => {
  const {
    successRedirectPath = '/lounge-survey/thank-you',
    onSuccess,
    onError,
  } = options;

  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: async (body: AnswersLoungeSurveyBody) => {
      const token = getLoungeToken();
      if (!token) {
        throw new Error('Session expired. Please log in again.');
      }
      return submitLoungeSurvey(body, token);
    },
    onSuccess: () => {
      onSuccess?.();
      navigate(successRedirectPath);
    },
    onError: (error: Error) => {
      // Check for specific API error messages
      if (error instanceof ApiError && error.status === 400) {
        const body = error.body;
        // Handle array response like ["Cannot survey an already completed/cancelled session"]
        if (Array.isArray(body) && body.length > 0 && typeof body[0] === 'string') {
          const customError = new Error(body[0]);
          onError?.(customError);
          return;
        }
      }
      onError?.(error);
    },
  });

  /**
   * Submit the survey with the given answers
   */
  const submit = useCallback(
    (answers: Map<number, number | null>, masseuseId?: number) => {
      // Build the request body from answers map
      const answersList = Array.from(answers.entries())
        .filter(([, value]) => value !== null && value !== 0)
        .map(([questionId, value]) => ({
          question: questionId,
          value: value as number,
        }));

      const body: AnswersLoungeSurveyBody = {
        answers: answersList,
        ...(masseuseId && { masseuse: masseuseId }),
      };

      mutation.mutate(body);
    },
    [mutation],
  );

  return {
    submit,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
  };
};

export default useLoungeSurveySubmit;
